'use client';

import * as React from 'react';
import { cn } from '../../utils/cn/cn';
import { Label } from '../Label/Label';
import { FeedbackNotification } from '../Notification/FeedbackNotification';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {}

// exporting needed for storybook
export interface CustomProps extends InputProps {
  containerClassName?: string;
  inputContainerClassName?: string;
  label?: string;
  error?: boolean;
  helperText?: string;
}

const Input = React.forwardRef<HTMLInputElement, CustomProps>(
  (
    {
      children,
      containerClassName,
      inputContainerClassName,
      label,
      error,
      helperText,
      className: inputClassName,
      id,
      ...props
    },
    ref,
  ) => {
    const fallBackId = React.useId();
    const helperTextId = React.useId();
    return (
      <div className={containerClassName}>
        {label && (
          <Label htmlFor={id || fallBackId} className="text-size-body2">
            {label}
          </Label>
        )}
        <div className={`relative ${inputContainerClassName || ''}`}>
          <input
            {...props}
            aria-errormessage={helperTextId}
            aria-describedby={helperTextId}
            aria-invalid={error ? 'true' : undefined}
            id={id || fallBackId}
            className={cn(
              'text-size-body1 border-color-grey-dark focus-visible:standard-focus-outline flex h-5.5 w-full rounded-sm border px-2 py-1.5 outline-offset-4 disabled:opacity-35',
              error
                ? 'border-color-error focus:input-error border-2'
                : 'focus:input-active focus:border-color-primary-dark',
              inputClassName,
            )}
            ref={ref}
          />
          {children}
        </div>
        <FeedbackNotification
          contentId={helperTextId}
          severity={error ? 'error' : 'info'}
          className="mt-0.5"
        >
          {helperText}
        </FeedbackNotification>
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input };
